import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { news, newsTypes, type NewsItem } from "@/mocks/news";

const PAGE_SIZE = 6;
const sortOptions = [
  { key: "newest", label: "最新发布" },
  { key: "oldest", label: "最早发布" },
] as const;

type SortKey = (typeof sortOptions)[number]["key"];

export default function News() {
  const [type, setType] = useState<NewsItem["type"] | "全部">("全部");
  const [sort, setSort] = useState<SortKey>("newest");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: "妖精漫画-漫画资讯与行业动态",
    description:
      "妖精漫画资讯频道，为你带来妖精题材漫画的最新新闻、作品介绍与行业动态，掌握奇幻漫画的一手消息。",
    keywords: "妖精漫画资讯,漫画新闻,妖精题材动态,作品介绍,行业动态",
  });

  useEffect(() => {
    setPage(1);
  }, [type, sort, keyword]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = news.filter((item) => {
      const typeOk = type === "全部" || item.type === type;
      const keyOk = !q || item.title.toLowerCase().includes(q) || item.excerpt.toLowerCase().includes(q);
      return typeOk && keyOk;
    });
    return [...list].sort((a, b) =>
      sort === "newest" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)
    );
  }, [type, sort, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "资讯" }]} />
          <h1 className="mt-4 font-heading text-2xl text-foreground-950 md:text-4xl">妖精漫画资讯</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            最新作品动态、新作介绍与行业观察，一次读懂妖精题材漫画圈的大小事。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:flex-wrap md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">类型</span>
            <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
              {(["全部", ...newsTypes] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`rounded-full px-3 py-1 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    type === t
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSort(opt.key)}
                  className={`rounded-full px-3 py-1 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    sort === opt.key
                      ? "bg-secondary-500 text-background-50"
                      : "text-foreground-600 hover:text-secondary-800"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索资讯标题"
                className="h-9 w-full rounded-full border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 md:w-56"
              />
            </div>
          </div>
        </div>

        <p className="mt-4 text-sm text-foreground-500">
          共 <span className="text-primary-600">{filtered.length}</span> 条资讯
        </p>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 flex flex-col gap-4">
              {pageItems.map((item) => (
                <NewsCard key={item.id} item={item} variant="list" />
              ))}
            </div>
            <div className="mt-10">
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <p className="text-base text-foreground-800">没有找到相关资讯</p>
            <p className="text-sm text-foreground-500">换个关键词或类型试试看吧。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setType("全部");
                setSort("newest");
              }}
              className="mt-2 rounded-md bg-secondary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-secondary-600 cursor-pointer whitespace-nowrap"
            >
              重置筛选条件
            </button>
          </div>
        )}

        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}